import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TreeSelect } from 'antd';
import { ICategory } from '@/models/categories.model';
import { StyledTreeSelect } from '../CategorySelect/CategorySelectStyles';
import { TreeSelectDataNode, highlightText, filterTreeNode } from '../../helpers';
import { NO_SUB_CATEGORIES_POSTFIX } from '../../constants';

type TCategoryMultiTreeSelect = {
  categories: ICategory[] | null;
  autocompleteCategories: ICategory[] | null;
  expandedItems: Set<string>;
  onSearch: (value?: string) => void;
  handleExpandTreeItemData: (dataNode: TreeSelectDataNode) => Promise<unknown>;
  value?: string[];
  onChange?: (value: string[]) => void;
  label?: string;
  disableInactive?: boolean;
  checkable?: boolean;
  disabled?: boolean;
  placeholder?: string;
};

const CategoryMultiTreeSelect = ({
  categories,
  autocompleteCategories,
  onSearch,
  expandedItems,
  handleExpandTreeItemData,
  value,
  onChange,
  label,
  disableInactive = false,
  checkable = false,
  disabled = false,
  placeholder,
}: TCategoryMultiTreeSelect) => {
  const { t } = useTranslation(['common']);
  const [searchValue, setSearchValue] = useState<string>('');

  const onInputChange = (v: string) => {
    setSearchValue(v);
    onSearch(v);
  };

  const handleInputClear = () => {
    setSearchValue('');
  };

  const isPlaceholderCategory = (category: ICategory) => category.id.endsWith(NO_SUB_CATEGORIES_POSTFIX);

  const hasRealSubCategories = (subCategories?: ICategory[]) =>
    !!subCategories?.some((subCategory) => !isPlaceholderCategory(subCategory));

  const isInactive = (c: ICategory) => disableInactive && c.active === false;

  const treeData = useMemo(() => {
    return (
      categories?.map((category) => {
        const hasRealChildren = hasRealSubCategories(category.sub_categories);

        return {
          value: category.id,
          title: category.name,
          isLeaf: !hasRealChildren,
          disabled: (!checkable && hasRealChildren) || isInactive(category),
          children: category.sub_categories?.map((subCategory) => {
            const subHasRealChildren = hasRealSubCategories(subCategory.sub_categories);

            if (isPlaceholderCategory(subCategory)) {
              return {
                value: subCategory.id,
                title: subCategory.name,
                isLeaf: true,
                disabled: true,
              };
            }

            return {
              value: subCategory.id,
              title: subCategory.name,
              isLeaf: !subHasRealChildren,
              disabled: (!checkable && subHasRealChildren) || isInactive(subCategory),
              children: subCategory.sub_categories?.map((lastLevelItem) => ({
                value: lastLevelItem.id,
                title: lastLevelItem.name,
                isLeaf: true,
                disabled: isPlaceholderCategory(lastLevelItem) || isInactive(lastLevelItem),
              })),
            };
          }),
        };
      }) || []
    );
  }, [categories, disableInactive, expandedItems]);

  const flatData = useMemo(
    () =>
      autocompleteCategories
        ? autocompleteCategories.map((category) => ({
            value: category.id,
            title: category.name,
            isLeaf: true,
            disabled:
              hasRealSubCategories(category.sub_categories) || isPlaceholderCategory(category) || isInactive(category),
          }))
        : [],
    [autocompleteCategories, disableInactive],
  );

  const titleRender = (node: any) => {
    const title = node?.title ?? node?.label ?? '';
    return highlightText(title.toString(), searchValue);
  };

  return (
    <div>
      {label ? <div style={{ marginBottom: 8 }}>{label}</div> : null}
      <StyledTreeSelect
        multiple
        showSearch
        disabled={disabled}
        treeCheckable={checkable}
        showCheckedStrategy={checkable ? TreeSelect.SHOW_CHILD : undefined}
        value={value}
        onChange={(v) => {
          if (!onChange) return;
          if (!v) return onChange([]);
          if (checkable) {
            onChange((v as { value: string }[]).map((item) => (typeof item === 'string' ? item : item.value)));
          } else {
            onChange(v as string[]);
          }
        }}
        onSearch={onInputChange}
        onClear={handleInputClear}
        placeholder={placeholder ?? t('common_placeholder_select')}
        dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
        allowClear
        treeDefaultExpandAll
        treeData={searchValue ? flatData : treeData}
        filterTreeNode={filterTreeNode}
        treeTitleRender={titleRender}
        loadData={handleExpandTreeItemData}
        maxTagCount="responsive"
      />
    </div>
  );
};

export default CategoryMultiTreeSelect;
